import React from "react";
import { Modal, Button } from "react-bootstrap";

export default function PrivacyPolicyModal({ open, onClose }) {
    return (
        <Modal
            show={open}
            onHide={onClose}
            centered
            size="xl"
            scrollable
            backdrop="static"
        >
            {/* Header */}
            <Modal.Header className="term-header-section">
                <h2>Privacy Policy</h2>
                <button onClick={onClose}>
                    <i className="fa fa-times" aria-hidden="true"></i>
                </button>
            </Modal.Header>

            {/* Body */}
            <Modal.Body style={{ maxHeight: "90vh" }}>
                <div className="term-content-section">

                    {/* <h4>Last Updated: 01/01/2026</h4> */}

                    <p>
                        This Privacy Policy explains how ClockMeNow (“Company,” “we,” “us,” or “our”) collects,
                        uses, discloses, and protects your information when you use the ClockMeNow mobile
                        application, web application, and related services (“ClockMeNow” or the “App”).
                    </p>

                    <p>
                        By accessing or using ClockMeNow, you agree to the practices described in this Privacy
                        Policy. If you do not agree, please discontinue use of the App.
                    </p>

                    {/* 1. Information We Collect */}
                    <h2>1. Information We Collect</h2>
                    <p>We may collect several types of information to operate and improve ClockMeNow:</p>

                    <h3>1.1 Information You Provide Directly</h3>

                    <h4>Account Information</h4>
                    <ul>
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Password (encrypted)</li>
                        <li>Profile details</li>
                    </ul>

                    <h4>Subscription & Billing Information</h4>
                    <ul>
                        <li>Payment method details (processed by secure third-party payment processors)</li>
                        <li>Billing address (if applicable)</li>
                        <li>Transaction history</li>
                    </ul>

                    <h4>Content You Create</h4>
                    <ul>
                        <li>Tasks, goals, schedules, reminders</li>
                        <li>Notes or text you enter</li>
                        <li>Tags, categories, productivity data</li>
                    </ul>

                    <h4>Support Communications</h4>
                    <ul>
                        <li>Emails, messages, surveys, or feedback submitted to our support team</li>
                    </ul>

                    {/* 1.2 Automatically Collected */}
                    <h3>1.2 Information Collected Automatically</h3>
                    <p>When you use ClockMeNow, we may automatically collect:</p>

                    <h4>Device Information</h4>
                    <ul>
                        <li>Device type, model, operating system</li>
                        <li>Browser type and version (web app)</li>
                        <li>Device identifiers</li>
                        <li>Mobile carrier</li>
                    </ul>

                    <h4>Usage Data</h4>
                    <ul>
                        <li>App interactions</li>
                        <li>Features used</li>
                        <li>Time spent</li>
                        <li>Settings and preferences</li>
                    </ul>

                    <h4>Log Data</h4>
                    <ul>
                        <li>IP address</li>
                        <li>Access times and dates</li>
                        <li>Crash data</li>
                        <li>Performance metrics</li>
                    </ul>

                    <h4>Cookies & Tracking Technologies</h4>
                    <ul>
                        <li>Authentication</li>
                        <li>Retaining user preferences</li>
                        <li>Analytics</li>
                        <li>Improving experience</li>
                    </ul>

                    {/* 1.3 Integrations */}
                    <h3>1.3 Information from Integrations (Optional)</h3>
                    <p>If you connect third-party services (e.g., calendars, cloud storage, login providers), we may receive:</p>

                    <ul>
                        <li>Calendar events</li>
                        <li>Authentication tokens</li>
                        <li>Sync metadata</li>
                        <li>Profile information (e.g., from Google or Apple sign-in)</li>
                    </ul>

                    <p>You control which permissions you grant, and you may disconnect integrations at any time.</p>

                    {/* 2. How We Use */}
                    <h2>2. How We Use Your Information</h2>

                    <h4>2.1 App Functionality</h4>
                    <ul>
                        <li>Provide, maintain, and improve ClockMeNow</li>
                        <li>Sync data across devices</li>
                        <li>Manage your account and subscriptions</li>
                        <li>Deliver reminders, notifications, and productivity insights</li>
                    </ul>

                    <h4>2.2 Personalization</h4>
                    <ul>
                        <li>Customize your dashboard or recommendations</li>
                        <li>Tailor features based on usage trends</li>
                    </ul>

                    <h4>2.3 Customer Support</h4>
                    <ul>
                        <li>Respond to requests, troubleshooting, and inquiries</li>
                        <li>Improve user experience based on feedback</li>
                    </ul>

                    <h4>2.4 Communication</h4>
                    <ul>
                        <li>Service-related alerts (password resets, billing notices)</li>
                        <li>Product updates or feature announcements</li>
                    </ul>

                    <h4>2.5 Analytics & Product Development</h4>
                    <ul>
                        <li>Analyze usage patterns</li>
                        <li>Enhance features, usability, and performance</li>
                    </ul>

                    <h4>2.6 Legal & Security</h4>
                    <ul>
                        <li>Detect and prevent fraud or misuse</li>
                        <li>Comply with legal obligations</li>
                        <li>Protect the rights and safety of users and the Company</li>
                    </ul>

                    {/* 3. How We Share */}
                    <h2>3. How We Share Your Information</h2>

                    <p>We never sell your personal data.</p>

                    <h4>3.1 Service Providers</h4>
                    <ul>
                        <li>Cloud hosting providers</li>
                        <li>Payment processors (e.g., Stripe, Apple, Google)</li>
                        <li>Analytics platforms</li>
                        <li>Email and notification services</li>
                        <li>Customer support tools</li>
                    </ul>

                    <h4>3.2 Third-Party Integrations (User-Enabled)</h4>
                    <p>Shared only according to permissions you grant.</p>

                    <h4>3.3 Legal Requirements</h4>
                    <p>We may disclose information if required by law or to protect safety.</p>

                    <h4>3.4 Business Transfers</h4>
                    <p>
                        If we undergo a merger, acquisition, or restructuring, data may be transferred as part of
                        the transaction.
                    </p>

                    {/* 4. Data Retention */}
                    <h2>4. Data Retention</h2>
                    <p>
                        We retain your data as long as your account is active, required to provide services, or
                        necessary to comply with legal obligations.
                    </p>

                    {/* 5 Security */}
                    <h2>5. How We Protect Your Information</h2>
                    <ul>
                        <li>Encrypted data transmission (HTTPS)</li>
                        <li>Secure cloud storage</li>
                        <li>Encrypted passwords</li>
                        <li>Access controls and authentication</li>
                        <li>Regular security reviews</li>
                    </ul>

                    {/* 6 Rights */}
                    <h2>6. Your Rights & Choices</h2>
                    <ul>
                        <li>Access your data</li>
                        <li>Correct or update information</li>
                        <li>Delete your account</li>
                        <li>Export or download your data</li>
                        <li>Opt out of marketing communications</li>
                        <li>Restrict or object to data processing</li>
                        <li>Withdraw consent</li>
                    </ul>

                    {/* 7 Children */}
                    <h2>7. Children’s Privacy</h2>
                    <p>
                        ClockMeNow is not intended for individuals under 13. We do not knowingly collect data
                        from children.
                    </p>

                    {/* 8 International */}
                    <h2>8. International Data Transfers</h2>
                    <p>
                        Your data may be processed in other countries. We take steps to ensure lawful transfer
                        compliance.
                    </p>

                    {/* 9 Third Party Links */}
                    <h2>9. Third-Party Links</h2>
                    <p>We are not responsible for privacy practices of external websites.</p>

                    {/* 10 Changes */}
                    <h2>10. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy periodically. Significant changes will be communicated
                        via email, app notifications, or posted updates.
                    </p>

                    {/* 11 Contact */}
                    <h2>11. Contact Us</h2>
                    <p>
                        AppWorks SC - ClockMeNow<br />
                        Email: support@clockmenow.com<br />
                        Address: P.O. Box 90088, Los Angeles, CA, 90009<br />
                        Website: www.clockmenow.com
                    </p>

                </div>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer>
                <Button className="modal-close-btn" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
